import {
  Datagrid,
  FunctionField,
  List,
  NumberInput,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';
import { Alert, Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import CheckroomIcon from '@mui/icons-material/Checkroom';

import { displayNameFor, formatCents } from './format';
import InventoryItemExpand from './InventoryItemExpand';
import { useInventoryConstants } from './useInventoryConstants';
import type { ConstantOption, InventoryItemRecord } from './types';

const firstImage = (record: InventoryItemRecord): string | undefined => {
  if (record.state === 'PHOTOGRAPHY_PENDING' && !record.isImageFromInventory) return undefined;
  const images = record.thumbnailImageUrls ?? record.imageUrls;
  if (!images) return undefined;
  return images.front ?? images.main ?? Object.values(images)[0];
};

type StateColor = 'success' | 'error' | 'warning' | 'info' | 'default';

const stateColor = (state: string): StateColor => {
  if (/REJECTED|DAMAGED|MISSING|SHRINK|LOST|PROBLEM|WRONG/.test(state)) return 'error';
  if (/APPROVED|CURATED$/.test(state)) return 'success';
  if (/ORDERED|PICK|PACK|SHIPPED|DROPPED/.test(state)) return 'info';
  if (/PENDING|NEEDS|REVIEW|IN_PROGRESS|HOLD|CLEANING|REPAIR|PROCESSING/.test(state)) return 'warning';
  return 'default';
};

const toChoices = (options: ConstantOption[]) =>
  options.map(({ code, displayName }) => ({ id: code, name: displayName }));

const InventoryItemList = () => {
  const { states, conditions, rejectReasons, isFallback } = useInventoryConstants();

  const filters = [
    <SearchInput key="searchQuery" source="searchQuery" placeholder="Search items" alwaysOn />,
    <SelectInput key="state" source="state" label="State" choices={toChoices(states)} />,
    <SelectInput key="condition" source="condition" label="Condition" choices={toChoices(conditions)} />,
    <TextInput key="sku" source="sku" label="SKU" />,
    <TextInput key="parentSku" source="parentSku" label="Parent SKU" />,
    <TextInput key="brand" source="brand" label="Brand" />,
    <NumberInput key="itemId" source="itemId" label="Item ID" />,
  ];

  return (
    <>
      {isFallback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Showing default filter options — live item states, conditions, and reject reasons
          couldn&apos;t be loaded, so filtering and item actions may be inaccurate.
        </Alert>
      )}
      <List
        resource="inventory-items"
        title="Inventory"
        filters={filters}
        sort={{ field: 'id', order: 'DESC' }}
        perPage={25}
        exporter={false}
      >
        <Datagrid
          bulkActionButtons={false}
          expand={(
            <InventoryItemExpand
              states={states}
              conditions={conditions}
              rejectReasons={rejectReasons}
            />
          )}
          rowClick="expand"
          sx={{
            '& .RaDatagrid-rowCell': { verticalAlign: 'middle' },
          }}
        >
          <FunctionField<InventoryItemRecord>
            source="id"
            label="Item"
            render={record => (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={firstImage(record)}
                  alt={record.catalogItem?.title ?? `Item ${record.id}`}
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: 'grey.100',
                    color: 'grey.500',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CheckroomIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {record.catalogItem?.title ?? `Item ${record.id}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {`#${record.id} · ${record.catalogItem?.sku ?? 'No SKU'}`}
                  </Typography>
                </Box>
              </Stack>
            )}
          />
          <TextField source="catalogItem.brand" label="Brand" sortable={false} emptyText="—" />
          <FunctionField<InventoryItemRecord>
            label="Size / Color"
            sortable={false}
            render={record =>
              [record.catalogItem?.size, record.catalogItem?.display_color ?? record.catalogItem?.color]
                .filter(Boolean)
                .join(' · ') || '—'}
          />
          <FunctionField<InventoryItemRecord>
            label="Condition"
            sortable={false}
            render={record => displayNameFor(conditions, record.condition)}
          />
          <FunctionField<InventoryItemRecord>
            label="State"
            sortable={false}
            render={record => (
              <Chip
                size="small"
                color={stateColor(record.state)}
                label={displayNameFor(states, record.state)}
              />
            )}
          />
          <FunctionField<InventoryItemRecord>
            label="Price"
            sortable={false}
            textAlign="right"
            render={record => (
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCents(record.price)}
              </Typography>
            )}
          />
          <TextField source="location.code" label="Location" sortable={false} emptyText="—" />
        </Datagrid>
      </List>
    </>
  );
};

export default InventoryItemList;
